import { ref } from 'vue'
import * as kycDocsApi from '../api/kycDocuments.js'

export function useKycDocumentUpload() {
  const uploading = ref(false)
  const uploadError = ref(null)
  const uploadedDocs = ref([]) // array of KycDocumentResponse

  /**
   * Run the full 3-step direct-to-R2 upload flow.
   * Returns the saved KycDocumentResponse on success.
   */
  async function uploadFile({ groupId, documentType, issuingCountry, file, frontSide = false }) {
    uploading.value = true
    uploadError.value = null

    try {
      // ── Step 1 - initialize ──────────────────────────────────────────────────
      const initResp = await kycDocsApi.initializeKycDocumentUpload(groupId, {
        documentType,
        issuingCountry,
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
        frontSide,
      })

      // ── Step 2 - direct PUT to R2 ────────────────────────────────────────────
      const putResp = await kycDocsApi.uploadDirectToR2(
        initResp.uploadUrl, file, initResp.allowedContentTypes || file.type
      )
      if (!putResp.ok) {
        const errText = await putResp.text()
        throw new Error(`R2 upload failed (${putResp.status}): ${errText}`)
      }

      // ── Step 3 - confirm with backend ────────────────────────────────────────
      const docResp = await kycDocsApi.confirmKycDocumentUpload(groupId, initResp.uploadSessionId, {
        sha256Hash: '',        // placeholder for future integrity check
        uploadedSizeBytes: file.size,
      })

      uploadedDocs.value = [...uploadedDocs.value, docResp]
      return docResp
    } catch (err) {
      uploadError.value = err?.message || 'Upload failed. Please try again.'
      throw err
    } finally {
      uploading.value = false
    }
  }

  async function fetchDocuments(groupId) {
    uploadError.value = null
    try {
      const docs = await kycDocsApi.fetchKycDocuments(groupId)
      uploadedDocs.value = docs
      return docs
    } catch (err) {
      uploadError.value = err?.message || 'Failed to load documents.'
      return []
    }
  }

  function reset() {
    uploadError.value = null
    uploading.value = false
  }

  return {
    uploading,
    uploadError,
    uploadedDocs,
    uploadFile,
    fetchDocuments,
    reset,
  }
}